import React, {
  FormEventHandler,
  useContext,
  useEffect,
  useState,
} from "react";
import DatePicker, {
  utils,
} from "@hassanmojab/react-modern-calendar-datepicker";

import { ProfileContext } from "../Context";
import { ProfileLayout } from "../Container/Layout";
import { Button } from "../Components/Base";
import { jalali_to_gregorian, Toast } from "../Utils";
import { TransactionUpdateProfile } from "../Services/Transactions";
import { textToObject, type DateObject } from "../Utils";

const Profile = () => {
  const { profile, setPersonalVerification, getProfile } =
    useContext(ProfileContext);

  // ======================================================================

  const [first_name, setFirst_name] = useState(profile.first_name);
  const [last_name, setLast_name] = useState(profile.last_name);
  const [fatherName, setFatherName] = useState(profile.fatherName || "");
  const [gender, setGender] = useState(profile.gender || "");
  const [nationalCode, setNationalCode] = useState(profile.nationalCode || "");
  const [city, setCity] = useState(profile.city || "");
  const [avatar, setAvatar] = useState(profile.profile || "");
  const [birthDay, setBirthDay] = useState<DateObject | null>(
    profile.birthDay ? textToObject(profile.birthDay) : null
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // ======================================================================

  const formIsValid = () => {
    return (
      first_name &&
      last_name &&
      fatherName &&
      gender &&
      city &&
      nationalCode.length == 10 &&
      birthDay &&
      avatar
    );
  };

  const handleSelectAvatar = (e: any) => {
    if (e.target.files.length) {
      setAvatar(e.target.files[0]);
    }
  };

  const handleSubmitForm: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (formIsValid()) {
      setIsLoading(true);
      const form = new FormData();
      form.append("first_name", first_name);
      form.append("last_name", last_name);
      form.append("fatherName", fatherName);
      form.append("gender", gender?.toString());
      form.append("city", city);
      form.append("nationalCode", nationalCode);
      form.append(
        "birthDay",
        jalali_to_gregorian(birthDay!.year, birthDay!.month, birthDay!.day)
      );
      if (typeof avatar != "string") {
        form.append("profile", avatar);
      }
      TransactionUpdateProfile(form)
        .then(({ data: res }) => {
          if (res.transaction_status) {
            Toast("success", "هویت شما با موفقیت احراز شد.");
            setPersonalVerification(true);
            getProfile();
          } else {
            Toast("error", res.message);
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  // ======================================================================

  useEffect(() => {
    setFirst_name(profile.first_name);
    setLast_name(profile.last_name);
    setFatherName(profile.fatherName || "");
    setGender(profile.gender || "");
    setNationalCode(profile.nationalCode || "");
    setCity(profile.city || "");
    setAvatar(profile.profile || "");
    setBirthDay(profile.birthDay ? textToObject(profile.birthDay) : null);
  }, [profile]);

  // ======================================================================

  return (
    <ProfileLayout>
      <div className="col-12">
        <div className="card border mb-4">
          <div className="card-header border-bottom p-3">
            <h4 className="card-header-title mb-0">حساب کاربری</h4>
          </div>
          <form onSubmit={handleSubmitForm} className="card-body">
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-center justify-content-lg-start">
                <div className="position-relative me-3">
                  <div className="position-absolute top-0 end-0 z-index-9">
                    <input
                      className="d-none"
                      onChange={handleSelectAvatar}
                      id="avatar-input"
                      type="file"
                    />
                    <label
                      htmlFor="avatar-input"
                      className="btn btn-sm btn-light btn-round mb-0 mt-n1 me-n1"
                    >
                      <i className="bi bi-pencil"></i>
                    </label>
                  </div>

                  <div className="avatar avatar-xl">
                    {avatar ? (
                      <img
                        className="avatar-img rounded-circle border border-white border-3 shadow"
                        src={
                          typeof avatar == "string"
                            ? avatar
                            : URL.createObjectURL(avatar)
                        }
                      />
                    ) : (
                      <span className="d-flex justify-content-center align-items-center avatar-img rounded-circle border border-white border-3 shadow">
                        <i className="fa opacity-25 fs-1 fa-user-circle" />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex input-group w-100 gap-2 mb-3">
              <div className="flex-grow-1">
                <label className="form-label">نام</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={first_name}
                    onChange={(e) => setFirst_name(e.target.value)}
                    placeholder="نام خود را وارد کنید"
                  />
                </div>
              </div>

              <div className="flex-grow-1">
                <label className="form-label">نام خانوادگی</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={last_name}
                    onChange={(e) => setLast_name(e.target.value)}
                    placeholder="نام خانوادگی خود را وارد کنید"
                  />
                </div>
              </div>
            </div>

            <div className="d-flex input-group w-100 gap-2 mb-3">
              <div className="flex-grow-1">
                <label className="form-label">نام پدر</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={fatherName}
                    placeholder="نام پدر خود را وارد کنید"
                    onChange={(e) => setFatherName(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex-grow-1">
                <label className="form-label">کد ملی</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={nationalCode}
                    onChange={(e) => setNationalCode(e.target.value)}
                    placeholder="کد ملی خود را وارد کنید"
                  />
                </div>
              </div>
            </div>

            <div className="d-flex input-group w-100 gap-2 mb-3">
              <div className="flex-grow-1">
                <label className="form-label">شهر</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={city}
                    placeholder="شهر خود را وارد کنید"
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex-grow-1">
                <label className="form-label">تاریخ تولد</label>
                <div className="input-group">
                  <DatePicker
                    value={birthDay}
                    locale="fa"
                    wrapperClassName="w-100 date-picker-height"
                    inputClassName="h-100 w-100 date-picker-border"
                    onChange={(e: DateObject) => setBirthDay(e)}
                    colorPrimary="var(--bs-primary)"
                    maximumDate={utils("fa").getToday()}
                    inputPlaceholder="تاریخ تولد خود را وارد کنید"
                  />
                </div>
              </div>
            </div>

            <div className="d-flex input-group w-100 gap-2 mb-3">
              <div className="flex-grow-1">
                <label className="form-label">جنسیت</label>
                <select
                  onChange={(e) => setGender(e.target.value)}
                  className="form-select cursor-pointer"
                  value={gender}
                >
                  <option value={0}>انتخاب کنید</option>
                  <option value={1}>آقا</option>
                  <option value={2}>خانم</option>
                </select>
              </div>
            </div>

            <div className="d-flex w-100 justify-content-end mt-4">
              <Button
                text="ذخیره"
                isLoading={isLoading}
                disabled={!formIsValid()}
                btnClass="btn w-100 btn-primary"
              />
            </div>
          </form>
        </div>
      </div>
    </ProfileLayout>
  );
};

export default Profile;
